// Quantity and remove links in the cart carry the line item key. With a discount code on the cart, Shopify gives the
// line a new key on every quantity change, so a second click before the drawer has re-rendered sent the old key and
// failed with "no valid id or line parameter". Clicks that arrive while a change is still running are held back and
// replayed on the re-rendered links; on the cart page, which reloads after each change, they are simply dropped.
//
// A held-back click is remembered by what it means (which variant, plus / minus / remove), not by where it was: the
// layout changes between clicks (a line disappears, "+" turns into a disabled button at the maximum quantity), and a
// positional replay would then hit a different product or the remove link.
//
// Wrapped in a block because the drawer re-inserts this script whenever it re-renders.
{
  if (!window.scCartLineGuard) {
    window.scCartLineGuard = true;

    const LINK_SELECTOR = 'line-item a[href*="/cart/change"]';
    // Longest a change may take before clicks are let through again, covering requests that fail without the theme
    // ever reporting the end of loading
    const SAFETY_TIMEOUT = 8000;

    let pending = false;
    let queued = null; // { variantId, occurrence, action, count }
    let safetyTimer;

    const params = (link) => new URL(link.href, window.location.origin).searchParams;

    const variantOf = (lineItem) =>
      (params(lineItem.querySelector('a[href*="/cart/change"]') || { href: '/' }).get('id') || '').split(':')[0];

    // The same variant can sit on several lines (different properties or selling plans), so a line is identified by
    // its variant plus which of those lines it is
    const drawerLinesOf = (variantId) =>
      [...document.querySelectorAll('cart-drawer line-item')].filter((item) => variantOf(item) === variantId);

    const describe = (link) => {
      const lineItem = link.closest('line-item');
      const { id, quantity } = Object.fromEntries(params(link));
      const current = Number(lineItem.querySelector('quantity-selector input')?.value);
      const target = Number(quantity);

      let action = 'minus';
      if (link.classList.contains('line-item__remove-button') || target === 0) action = 'remove';
      else if (Number.isFinite(current) && target > current) action = 'plus';

      const variantId = (id || '').split(':')[0];
      return { variantId, occurrence: drawerLinesOf(variantId).indexOf(lineItem), action };
    };

    // The link that does the same thing on the re-rendered line, or null when it no longer exists
    const findLink = ({ variantId, occurrence, action }) => {
      const lineItem = drawerLinesOf(variantId)[occurrence];
      if (!lineItem) return null;

      return (
        [...lineItem.querySelectorAll('a[href*="/cart/change"]')].find((link) => describe(link).action === action) ||
        null
      );
    };

    const settle = () => {
      clearTimeout(safetyTimer);
      pending = false;
    };

    const release = () => {
      settle();
      if (!queued) return;

      const next = queued;
      queued = next.count > 1 ? { ...next, count: next.count - 1 } : null;

      // Removing is never repeated, and a click whose target is gone (line removed, maximum reached) is dropped
      const link = findLink(next);
      if (!link || next.action === 'remove') queued = null;
      if (link) link.click();
    };

    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest(LINK_SELECTOR);
        if (!link) return;

        if (!pending) {
          pending = true;
          clearTimeout(safetyTimer);
          safetyTimer = setTimeout(() => {
            queued = null;
            settle();
          }, SAFETY_TIMEOUT);
          return; // the theme handles this click as usual
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        if (!link.closest('cart-drawer')) return;

        const intent = describe(link);
        queued =
          queued &&
          queued.variantId === intent.variantId &&
          queued.occurrence === intent.occurrence &&
          queued.action === intent.action
            ? { ...queued, count: queued.count + 1 }
            : { ...intent, count: 1 };
      },
      true
    );

    // Success: the drawer announces the re-render with fresh keys
    document.addEventListener('cart-drawer:refreshed', () => {
      if (pending) release();
    });

    // A rejected change never re-renders, so stop waiting shortly after the request has finished
    document.addEventListener('theme:loading:end', () => {
      if (!pending) return;
      clearTimeout(safetyTimer);
      safetyTimer = setTimeout(() => {
        queued = null;
        settle();
      }, 2000);
    });
  }
}
